/**
 * Order Management Page - Comprehensive order view and management
 * Displays all orders across brands with advanced filtering and actions
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  InlineGrid,
  Badge,
  Button,
  Spinner,
  Banner,
  EmptyState,
  IndexTable,
  useIndexResourceState,
  Tooltip,
  Divider,
  Filters,
  ChoiceList,
  RangeSlider,
  TextField,
} from '@shopify/polaris';
import {
  FilterIcon,
  RefreshIcon,
  OrderIcon,
  CashDollarIcon,
  ViewIcon,
  ExternalIcon,
} from '@shopify/polaris-icons';

import { useBrands, useAllBrandOrders, useMarketplacesList } from '../utils/queries';
import apiClient from '../utils/api';
import OrderDetailPanel from '../components/OrderDetailPanel';
import { ORDER_STATUS_OPTIONS } from '../utils/dataValidation';
import { DEFAULT_PAGE_SIZE, PageSizeSelect } from '../utils/table';

/**
 * Was this order created by the 2W app?
 *
 * A case-insensitive PREFIX test, not an equality check, because `Order.appName` is not ours to
 * choose — it is read verbatim from Shopify (`orderData.app?.name`,
 * unified-sync-service.js:782), so its value is whatever the Shopify app happens to be called:
 *
 *   production   "2w"                        (shopify.app.toml)
 *   development  "2w-connect-development"    (shopify.app.development.toml)
 *   seeded data  "2W"
 *
 * This used to be `order.appName === '2W'`, which matches none of the three in production or
 * development — so every 2W-created order whose MiraklOrder link was missing fell to the `info`
 * tone and was presented as though 2W had not brought it in.
 */
const isTwoWApp = (appName) => String(appName || '').trim().toLowerCase().startsWith('2w');

// Helper function to format date as YYYY-MM-DD without timezone shift
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * The order detail panel's five sections, exactly as the wireframe specifies:
 * Identity · Status · Fulfillment & tracking · Fees & payout · Weight.
 *
 * A retailer-side value is null for any order that did not come through the connector —
 * an Online Store order genuinely has no retailer state — so those rows say so rather
 * than showing a blank that reads like missing data.
 *
 * "Tracking sent to retailer" is the reason this panel is worth opening. Fulfillment and
 * tracking numbers are already visible in Shopify; whether the tracking number reached
 * the retailer is the only thing 2W knows and nobody else does, and it is what stands
 * between the brand and a late-shipment penalty. OR23 and OR24 are separate calls that
 * fail independently, so the row distinguishes sent from confirmed.
 */
/**
 * The Orders table's columns — ONE definition, with everything else about a column derived
 * from it.
 *
 * WHY THIS IS ONE ARRAY OF OBJECTS AND NOT THREE PARALLEL ARRAYS INDEXED BY POSITION. It was
 * three: `headings` held the titles, a `sortFields` array inside handleSort held the API field
 * names, and a `sortable` literal held the flags. `sortFields` was SHORTER than `headings` —
 * `Source` had no entry — so every heading from Source rightwards sent the field belonging to
 * the column on its left (Status sorted by weight, Weight by item count, Gross sales by
 * refunds...), and the last two sent `undefined`, which cleared the sort and snapped the arrow
 * back to column 0. That misalignment happened twice. Here a column carries its own sort
 * field, and the heading list, the sortable flags and the toggle labels are all read off this
 * one array, so there is no second list to fall out of step.
 *
 * `sortField` is the name GET /orders accepts. Checked against that endpoint's own whitelist
 * (2w-connect web/modules/admin/billing/controllers/orders.controller.js, sortFieldMapping) —
 * anything outside it is mapped to `order_date`, so a plausible-looking name is a silent no-op
 * under a heading whose arrow says the sort took.
 *
 * A column with NO `sortField` is NOT sortable, deliberately, because the endpoint has no
 * field for it: Source (`appName`), Status (`financialStatus`) and Items (`totalItems`) are
 * selected by the query but absent from its whitelist, and neither commission column has a
 * whitelisted field of its own — `appliedCommissionAmount` is whitelisted but holds the SUM of
 * the marketplace and 2W shares (unified-sync-service.js: appliedMarketplaceCommissionAmount +
 * applied2wCommissionAmount), so it orders by neither of the two figures on screen. A heading
 * that cannot really sort shows no arrow rather than a broken one.
 *
 * `sortWords` becomes sortToggleLabels, which is REQUIRED for a heading tooltip to render on a
 * sortable column: Polaris reads sortToggleLabels[index][direction] unguarded while building
 * one (IndexTable.js:477) and, with the prop absent, returns the bare sort button while
 * silently dropping tooltipContent. Deriving the map from this array means a sortable column
 * cannot exist without its labels and a column that stops being sortable cannot leave a stale
 * entry behind. Polaris's own defaults are "A-Z"/"Z-A" for text and "Ascending"/"Descending"
 * for everything else; these follow that convention but are worded per column so the announced
 * label describes the actual values.
 */
export const ORDER_COLUMNS = [
  { title: 'Order', sortField: 'orderNumber', sortWords: { ascending: 'A-Z', descending: 'Z-A' } },
  { title: 'Date', sortField: 'orderDate', sortWords: { ascending: 'Oldest first', descending: 'Newest first' } },
  { title: 'Brand', sortField: 'brandId', sortWords: { ascending: 'A-Z', descending: 'Z-A' } },
  // "Retailer", not "Marketplace". They are retailers everywhere else in the console and in
  // the detail panel beside this table, and one screen using the other word for the same thing
  // is how a reader concludes they are two things. `sortField` keeps the API's own key.
  { title: 'Retailer', sortField: 'marketplace', sortWords: { ascending: 'A-Z', descending: 'Z-A' } },
  { title: 'Source' },
  { title: 'Status' },
  { title: 'Weight', sortField: 'totalWeight', sortWords: { ascending: 'Lightest first', descending: 'Heaviest first' } },
  { title: 'Items' },
  // 'Gross sales', not 'Sales'. This column renders grossAmount, which is BEFORE
  // discounts, while Analytics -> Marketplace Analysis reports a column also called
  // "Sales" computed as grossAmount - discountAmount. Same word, same order, two
  // different numbers on two admin screens — 201.90 here against 171.62 there on a
  // discounted order, with nothing on either screen to explain the gap.
  { title: 'Gross sales', sortField: 'grossAmount', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  { title: 'Refund', sortField: 'refundAmount', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  { title: 'Net Sales', sortField: 'netAmount', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  {
    // Kept as "Marketplace Commission" deliberately — that wording, and "Marketplace
    // Rate", are the two agreed exceptions to calling them retailers.
    //
    // The tooltip exists because a SECOND commission number for the same order lives on
    // the brand's Retail Connect tab, and the two are not the same thing:
    //   HERE  our calculation — a rate configured in 2W applied to Shopify's net amount
    //         (unified-sync-service.js:1193). What we EXPECT the deduction to be.
    //   THERE MiraklOrder.totalCommission — the retailer's own reported figure from OR11
    //         (mirakl-order-service.js:165). What the retailer SAYS it is deducting.
    // They can differ, most obviously when the configured rate is stale, and nothing
    // reconciles them. So each says which it is.
    //
    // Not sortable: the endpoint whitelists no field for this number. See above.
    title: 'Marketplace Commission',
    tip:
      "2W's own calculation: the marketplace rate configured for this brand and retailer, "
      + "applied to net sales. It is not the retailer's reported deduction — for that, open "
      + "the order on the brand's Retail Connect tab, where the retailer's own figure is shown "
      + 'as Retailer commission. The two can differ.',
  },
  { title: '2W Commission' },
  {
    title: 'Shipped by',
    sortField: 'shippedBy',
    sortWords: { ascending: 'A-Z', descending: 'Z-A' },
    tip: 'Who shipped the order, worked out from the fulfilment location against the 2W '
      + 'locations ticked on the brand. An order the brand shipped itself is charged no '
      + 'shipping fee. Blank means it is not fulfilled, or the location could not be read - '
      + 'both of which are charged as they always were.',
  },
  { title: 'Shipping Fees', sortField: 'appliedShippingFee', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  { title: 'Return Fees', sortField: 'appliedReturnFee', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  { title: 'Net Payout', sortField: 'payout', sortWords: { ascending: 'Lowest first', descending: 'Highest first' } },
  { title: 'Actions' },
];

/** Headings, left aligned as the wireframe has them, with each column's own tip attached. */
export const ORDER_HEADINGS = ORDER_COLUMNS.map(({ title, tip }) => ({
  title,
  alignment: 'start',
  ...(tip ? { tooltipContent: tip, tooltipWidth: 'wide' } : {}),
}));

/** Sortable exactly where a server field exists — nowhere else. */
export const ORDER_SORTABLE = ORDER_COLUMNS.map((c) => Boolean(c.sortField));

/**
 * Complete and aligned by construction: an entry for every sortable index and none for any
 * other, because both facts come from the same `sortField`.
 */
export const SORT_TOGGLE_LABELS = Object.fromEntries(
  ORDER_COLUMNS
    .map((c, i) => [i, c.sortField ? c.sortWords : null])
    .filter(([, words]) => Boolean(words)),
);

/**
 * Which heading carries the arrow, looked up by the field in state rather than matched on
 * heading text. The old version fell back to a substring test of the title against `sortBy`
 * and then listed a special case per column — one of which ('appliedCommissionRate' against a
 * "Commission" heading) named a column that no longer exists, so that sort showed its arrow on
 * whatever title happened to contain the word.
 */
export const orderSortColumnIndex = (sortBy) => {
  const index = ORDER_COLUMNS.findIndex((c) => c.sortField === sortBy);
  return index === -1 ? 0 : index;
};

export default function OrderManagement() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  // Source: retailer orders vs orders placed directly in the store. Two sources feed
  // this table now, and without this an admin cannot separate them.
  const [sourceFilter, setSourceFilter] = useState([]);
  /**
   * Tracking state, seeded from the query string.
   *
   * The Dashboard's "fulfilled retailer orders with no tracking number" alert links straight
   * to this page with ?tracking=missing, so the admin lands on the orders behind the number
   * rather than on an unfiltered table they have to narrow themselves. Read once on mount:
   * after that the picker owns it, and re-reading would fight the admin clearing it.
   */
  const [trackingFilter, setTrackingFilter] = useState(
    () => (new URLSearchParams(window.location.search).get('tracking') === 'missing' ? ['missing'] : []),
  );
  const [sortBy, setSortBy] = useState('orderDate');
  const [sortDirection, setSortDirection] = useState('descending');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState([]);
  // Orders whose shipping fee nobody has settled. Its own filter rather than a Status
  // value: Status is what Shopify says happened to the order, this is whether OUR figure
  // is finished, and combining them would make "fulfilled AND needs review" unaskable.
  const [shippingReviewFilter, setShippingReviewFilter] = useState([]);
  const [marketplaceFilter, setMarketplaceFilter] = useState([]);
  const [brandFilter, setBrandFilter] = useState([]);
  const [amountRange, setAmountRange] = useState([0, 5000]);

  // Date range filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  /**
   * Rows per page — and, on this screen, the API's `limit`.
   *
   * This table is paged SERVER-SIDE: GET /orders takes `page` and `limit` and answers with
   * `pagination.totalOrders` / `totalPages`, so the size chosen here belongs in `queryParams`
   * and the request re-fires (the params are the react-query key). Slicing the returned array
   * instead would show 5 of the 50 rows the server sent while the footer still counted pages
   * of 50 — wrong in both directions. Nothing is clamped locally for the same reason: the
   * server decides what page 1 holds.
   *
   * Seeded at DEFAULT_PAGE_SIZE, which is the 10 this page has always requested.
   */
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);

  /**
   * A new row count restarts at page 1, like every filter and the sort do. Page 4 of a 10-row
   * window is inside page 1 of a 50-row one, so keeping the number would skip 150 orders and
   * can land past the end of the list entirely — which the server answers with no rows at all.
   */
  const handlePageSizeChange = useCallback((size) => {
    setItemsPerPage(size);
    setCurrentPage(1);
  }, []);

  // Debounce search query to reduce API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Build query parameters for API
  const queryParams = useMemo(() => ({
    page: currentPage,
    limit: itemsPerPage,
    sortBy,
    sortDirection,
    search: debouncedSearchQuery || undefined,
    source: sourceFilter.length > 0 ? sourceFilter.join(',') : undefined,
    tracking: trackingFilter.length > 0 ? trackingFilter[0] : undefined,
    statusFilter: statusFilter.length > 0 ? statusFilter : undefined,
    shippingFeeReview: shippingReviewFilter.length > 0 ? 'true' : undefined,
    marketplace: marketplaceFilter.length > 0 ? marketplaceFilter : undefined,
    brandId: brandFilter.length > 0 ? brandFilter : undefined,
    amountMin: amountRange[0] > 0 ? amountRange[0] : undefined,
    amountMax: amountRange[1] < 5000 ? amountRange[1] : undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  }), [currentPage, itemsPerPage, sortBy, sortDirection, debouncedSearchQuery, sourceFilter, trackingFilter, statusFilter, shippingReviewFilter, marketplaceFilter, brandFilter, amountRange, startDate, endDate]);

  // Query hooks
  const { data: brandsResponse, isLoading: brandsLoading } = useBrands();
  const brands = brandsResponse?.brands || brandsResponse || [];
  const {
    data: ordersData,
    isLoading: ordersLoading,
    error: ordersError,
    refetch: refetchOrders
  } = useAllBrandOrders(queryParams);

  // Fetch all marketplaces for filter (independent of current page orders)
  const { data: marketplacesData, isLoading: marketplacesLoading } = useMarketplacesList('all');

  // Extract orders and pagination from response
  const allOrders = ordersData?.orders || [];

  // Separate summary row from regular orders
  const summaryRow = allOrders.find(order => order.isSummaryRow);
  const orders = allOrders.filter(order => !order.isSummaryRow);

  const pagination = ordersData?.pagination || {};
  const summary = ordersData?.summary || null;
  const totalItems = pagination.totalOrders || 0;
  const totalPages = pagination.totalPages || 0;

  // Get all marketplaces from dedicated API endpoint (not from current page orders)
  const marketplaceOptions = useMemo(() => {
    const marketplaces = marketplacesData?.marketplaces || [];
    return marketplaces.map(mp => ({
      label: mp,
      value: mp
    }));
  }, [marketplacesData]);

  // Brand options for filter
  const brandOptions = useMemo(() => {
    return brands.map(brand => ({
      label: brand.name,
      value: brand.id
    }));
  }, [brands]);

  const resourceName = {
    singular: 'order',
    plural: 'orders',
  };

  // The table is `selectable={false}` and the selectedItemsCount/onSelectionChange props were
  // commented out, so `allResourcesSelected` and `handleSelectionChange` could never affect
  // anything. Only the ids are still read (for row keys/lookups), so keep just those.
  const { selectedResources } = useIndexResourceState(orders);

  // Filter options
  const sourceOptions = [
    { label: 'Retailer orders', value: 'retailer' },
    { label: 'Direct / Online Store', value: 'direct' },
  ];

  // Single shared copy of the server's accepted statusFilter set - see ORDER_STATUS_VALUES in
  // utils/dataValidation.js. AUTHORIZED and VOIDED were missing from the local list here, so
  // orders in those states were rendered but could never be filtered for.
  const statusOptions = ORDER_STATUS_OPTIONS;

  // Applied filters
  const appliedFilters = [];
  if (searchQuery) {
    appliedFilters.push({
      key: 'search',
      label: `Search: ${searchQuery}`,
      onRemove: () => setSearchQuery(''),
    });
  }
  if (shippingReviewFilter.length > 0) {
    appliedFilters.push({
      key: 'shippingFeeReview',
      label: 'Shipping fee: needs review',
      onRemove: () => setShippingReviewFilter([]),
    });
  }
  if (statusFilter.length > 0) {
    appliedFilters.push({
      key: 'status',
      // statusFilter holds the server's raw values (PARTIALLY_REFUNDED, …). Show the same
      // presentation labels the ChoiceList uses, not the enum.
      label: `Status: ${statusFilter.map((v) => statusOptions.find((o) => o.value === v)?.label || v).join(', ')}`,
      onRemove: () => setStatusFilter([]),
    });
  }
  if (sourceFilter.length > 0) {
    appliedFilters.push({
      key: 'source',
      label: `Source: ${sourceFilter.map((s) => (s === 'retailer' ? 'Retailer orders' : 'Direct / Online Store')).join(', ')}`,
      onRemove: () => setSourceFilter([]),
    });
  }
  if (trackingFilter.length > 0) {
    appliedFilters.push({
      key: 'tracking',
      label: 'Tracking: missing',
      onRemove: () => setTrackingFilter([]),
    });
  }
  if (marketplaceFilter.length > 0) {
    appliedFilters.push({
      key: 'marketplace',
      label: `Marketplace: ${marketplaceFilter.join(', ')}`,
      onRemove: () => setMarketplaceFilter([]),
    });
  }
  if (brandFilter.length > 0) {
    const brandLabels = brandFilter.map(id => 
      brands.find(b => b.id === id)?.name || id
    ).join(', ');
    appliedFilters.push({
      key: 'brand',
      label: `Brand: ${brandLabels}`,
      onRemove: () => setBrandFilter([]),
    });
  }
  if (amountRange[0] > 0 || amountRange[1] < 5000) {
    appliedFilters.push({
      key: 'amount',
      label: `Amount: $${amountRange[0]} - $${amountRange[1]}`,
      onRemove: () => setAmountRange([0, 5000]),
    });
  }
  if (startDate || endDate) {
    const dateLabel = startDate && endDate
      ? `Date: ${startDate} to ${endDate}`
      : startDate
        ? `Date: From ${startDate}`
        : `Date: Until ${endDate}`;
    appliedFilters.push({
      key: 'dateRange',
      label: dateLabel,
      onRemove: () => {
        setStartDate('');
        setEndDate('');
      },
    });
  }

  const filters = [
    {
      key: 'shippingFeeReview',
      label: 'Shipping fee',
      filter: (
        <ChoiceList
          title="Shipping fee"
          titleHidden
          choices={[{ label: 'Needs review', value: 'needs-review' }]}
          selected={shippingReviewFilter}
          onChange={setShippingReviewFilter}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={statusOptions}
          selected={statusFilter}
          onChange={setStatusFilter}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'source',
      label: 'Source',
      filter: (
        <ChoiceList
          title="Source"
          titleHidden
          choices={sourceOptions}
          selected={sourceFilter}
          onChange={setSourceFilter}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'tracking',
      label: 'Tracking',
      filter: (
        // One choice, not a pair. "Has tracking" would be the overwhelming majority of every
        // table and nobody goes looking for it; the reason to filter here is to find the
        // orders that are missing one.
        <ChoiceList
          title="Tracking"
          titleHidden
          choices={[{
            label: 'Missing tracking number',
            value: 'missing',
            helpText: 'Retailer orders fulfilled with no tracking number to send on.',
          }]}
          selected={trackingFilter}
          onChange={setTrackingFilter}
        />
      ),
      shortcut: true,
    },
    {
      key: 'marketplace',
      label: 'Retailer',
      filter: (
        // marketplacesLoading was fetched and never used, so while /marketplaces/list was in
        // flight this rendered an empty ChoiceList that reads as "no retailers exist". Say which
        // it is.
        marketplacesLoading ? (
          <Box padding="200">
            <InlineStack gap="200" blockAlign="center">
              <Spinner size="small" />
              <Text variant="bodySm" tone="subdued">Loading retailers…</Text>
            </InlineStack>
          </Box>
        ) : marketplaceOptions.length === 0 ? (
          <Box padding="200">
            <Text variant="bodySm" tone="subdued">No retailers found.</Text>
          </Box>
        ) : (
          <ChoiceList
            title="Retailer"
            titleHidden
            choices={marketplaceOptions}
            selected={marketplaceFilter}
            onChange={setMarketplaceFilter}
            allowMultiple
          />
        )
      ),
      shortcut: true,
    },
    {
      key: 'brand',
      label: 'Brand',
      filter: (
        <ChoiceList
          title="Brand"
          titleHidden
          choices={brandOptions}
          selected={brandFilter}
          onChange={setBrandFilter}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'amount',
      label: 'Amount Range',
      filter: (
        <RangeSlider
          label="Net Amount Range"
          value={amountRange}
          onChange={setAmountRange}
          output
          min={0}
          max={5000}
          step={50}
          prefix="$"
        />
      ),
    },
    {
      key: 'dateRange',
      label: 'Date Range',
      filter: (
        <BlockStack gap="300">
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={setStartDate}
            autoComplete="off"
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={setEndDate}
            autoComplete="off"
            min={startDate}
          />
          <Button
            size="slim"
            onClick={() => {
              const today = new Date();
              const thirtyDaysAgo = new Date(today);
              thirtyDaysAgo.setDate(today.getDate() - 30);
              setStartDate(formatDateLocal(thirtyDaysAgo));
              setEndDate(formatDateLocal(today));
            }}
          >
            Last 30 days
          </Button>
        </BlockStack>
      ),
    },
  ];

  // Clear all filters
  const handleFiltersClearAll = useCallback(() => {
    setSearchQuery('');
    setStatusFilter([]);
    setSourceFilter([]);
    setTrackingFilter([]);
    setMarketplaceFilter([]);
    setBrandFilter([]);
    setAmountRange([0, 5000]);
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  }, []);

  /**
   * Heading click -> API sort field, read off ORDER_COLUMNS by the index Polaris hands us.
   *
   * No local list of field names: that list was one entry short of the headings, so ten of
   * the sixteen sortable columns sent a neighbour's field and the last two sent `undefined`.
   * A column with no `sortField` is not marked sortable, so this guard should be unreachable
   * — it is here because writing `undefined` into `sortBy` is what cleared the sort and reset
   * the arrow to column 0, and that must not be one prop edit away again.
   */
  const handleSort = useCallback((headingIndex, direction) => {
    const field = ORDER_COLUMNS[headingIndex]?.sortField;
    if (!field) return;
    setSortBy(field);
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  // Order details modal
  const selectedOrder = orders.find(order => order.id === selectedOrderId);

  const handleOrderSelect = useCallback((orderId) => {
    setSelectedOrderId(orderId);
    setOrderDetailOpen(true);
  }, []);


  // Pagination handlers
  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);


  // Table rows - includes both orders and refund line items (memoized for performance)
  const rowMarkup = useMemo(() => {
    const allRows = orders.flatMap((order, orderIndex) => {
    const rows = [];
    const getStatusTone = (status) => {
      switch (status) {
        case 'paid': return 'success';
        case 'pending': return 'attention';
        case 'cancelled': return 'critical';
        case 'refunded': return 'warning';
        case 'partially_refunded': return 'warning';
        default: return 'info';
      }
    };

    const getTone = (value) => {
      if (value < 0) return 'critical';
      if (value > 0) return 'success';
      return 'base';
    };

    // Add main order row
    rows.push(
      <IndexTable.Row
        id={order.id}
        key={order.id}
        selected={selectedResources.includes(order.id)}
        position={orderIndex}
        // Tints the whole row, not just the fee cell, so an unfinished figure is visible
        // while scanning rather than only once somebody reads that column. 'warning' and
        // not 'caution': IndexTable.Row takes subdued | success | warning | critical, and
        // anything else is silently ignored.
        tone={order.shippingFeeNeedsReview ? 'warning' : undefined}
      >
        {/* Order */}
        <IndexTable.Cell>
          <BlockStack gap="050">
            <Text variant="bodyMd" fontWeight="semibold">
              {order.orderNumber}
            </Text>
            {/* IN THE FIRST COLUMN ON PURPOSE. The row tone Polaris applies paints the
                row element and only the first, second and last cells; every cell between
                keeps its own opaque background and covers it. On a table this wide those
                tinted edges are scrolled off, which is why the tint appears to show only
                on hover - hover is the one state that paints every cell. This column is
                always on screen. */}
            {order.shippingFeeNeedsReview && (
              <Badge tone="warning">Shipping fee needs review</Badge>
            )}
            {/* The retailer's own order id, shown only when there is one. When a
                retailer emails about MKL-4521 this is what an admin looks for, and it
                is matched by the search box above. */}
            {order.miraklOrderId && (
              <Text variant="bodySm" tone="subdued">
                <span style={{ fontFamily: 'var(--p-font-family-mono)' }}>
                  {order.miraklOrderId}
                </span>
              </Text>
            )}
          </BlockStack>
        </IndexTable.Cell>

        {/* Date */}
        <IndexTable.Cell>
          <Text variant="bodyMd">
            {order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            }) : 'N/A'}
          </Text>
        </IndexTable.Cell>

        {/* Brand */}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="medium">
            {order.brand?.name || 'Unknown'}
          </Text>
        </IndexTable.Cell>

        {/* Marketplace */}
        <IndexTable.Cell>
          {/* Neutral for orders 2W brought in, info for anything else — the wireframe
              distinguishes the two so a retailer order is visually separable from an
              Online Store one at a glance. */}
          <Badge tone={order.isRetailerOrder || isTwoWApp(order.appName) ? undefined : 'info'}>
            {order.marketplace || 'Direct'}
          </Badge>
        </IndexTable.Cell>

        {/* Source */}
        <IndexTable.Cell>
          <Text variant="bodySm" tone="subdued">
            {order.appName || '—'}
          </Text>
        </IndexTable.Cell>

        {/* Status */}
        <IndexTable.Cell>
          <Badge tone={getStatusTone(order.financialStatus)}>
            {order.financialStatus || 'Unknown'}
          </Badge>
        </IndexTable.Cell>

        {/* Weight */}
        <IndexTable.Cell>
          <Text variant="bodyMd">
            {order.totalWeight ? apiClient.formatWeight(order.totalWeight) : '-'}
          </Text>
        </IndexTable.Cell>

        {/* Items (NEW) */}
        <IndexTable.Cell>
          <Text variant="bodyMd">
            {order.totalItems || 0}
          </Text>
        </IndexTable.Cell>

        {/* Sales (renamed from Gross Revenue - value is gross, discounts not subtracted) */}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="medium">
            {apiClient.formatCurrency(order.grossAmount || 0)}
          </Text>
        </IndexTable.Cell>

        {/* Refund*/}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="medium">
            {apiClient.formatCurrency(order.refundAmount || 0)}
          </Text>
        </IndexTable.Cell>

        {/* Net Sales */}
        <IndexTable.Cell>
          <Text variant="bodyMd" fontWeight="medium" tone="success">
            {apiClient.formatCurrency(order.netAmount || 0)}
          </Text>
        </IndexTable.Cell>

        {/* MKT Commission */}
        <IndexTable.Cell>
          <BlockStack gap="100">
            <Text tone={getTone(order.appliedMarketplaceCommissionAmount)} variant="bodyMd">
              {apiClient.formatCurrency(order.appliedMarketplaceCommissionAmount ?? 0)}
            </Text>
            <Text variant="bodySm" tone="subdued">
              {apiClient.formatPercentage(order.appliedMarketplaceCommissionRate || 0)}
            </Text>
          </BlockStack>
        </IndexTable.Cell>

        {/* 2W Commission */}
        <IndexTable.Cell>
          <BlockStack gap="100">
            <Text tone={getTone(order.applied2wCommissionAmount)} variant="bodyMd">
              {apiClient.formatCurrency(order.applied2wCommissionAmount ?? 0)}
            </Text>
            <Text variant="bodySm" tone="subdued">
              {apiClient.formatPercentage(order.applied2wCommissionRate ?? 0)}
            </Text>
          </BlockStack>
        </IndexTable.Cell>

        {/* Shipped by. Beside the fee it explains: a $0.00 shipping fee is otherwise
            indistinguishable from an order that was never priced. The location name is
            shown underneath for 2W staff - it is deliberately absent from the brand-facing
            Billing page, which gets the label alone. */}
        <IndexTable.Cell>
          {order.shippedByLabel ? (
            <BlockStack gap="050">
              <Text variant="bodyMd">{order.shippedByLabel}</Text>
              {order.fulfilledFrom && (
                <Text variant="bodySm" tone="subdued">{order.fulfilledFrom}</Text>
              )}
            </BlockStack>
          ) : (
            <Text variant="bodyMd" tone="subdued">—</Text>
          )}
        </IndexTable.Cell>

        {/* Shipping Fees */}
        <IndexTable.Cell>
          <BlockStack gap="050">
            <Text tone={getTone(order.appliedShippingFee || 0)} variant="bodyMd">
              {apiClient.formatCurrency(order.appliedShippingFee || 0)}
            </Text>
            {/* Beneath the figure it explains, so the two are read together. Only ever
                rendered for a fee nobody finished working out - the statuses that are
                answers carry no label, which is what keeps this off ordinary rows. */}
            {order.shippingFeeStatusLabel && (
              <Text variant="bodySm" tone="caution">
                {order.shippingFeeStatusLabel}
              </Text>
            )}
            {/* The stored fee and the invoice disagree until somebody acts on this, which
                is the whole point of holding it - but only if the row says so. */}
            {order.shippingFeePendingFallback > 0 && (
              <Text variant="bodySm" tone="subdued">
                {apiClient.formatCurrency(order.shippingFeePendingFallback)} fallback will apply
              </Text>
            )}
          </BlockStack>
        </IndexTable.Cell>

        {/* Return Fees */}
        <IndexTable.Cell>
          <Text tone={getTone(order.appliedReturnFee || 0)}  variant="bodyMd">
            {apiClient.formatCurrency(order.appliedReturnFee || 0)}
          </Text>
        </IndexTable.Cell>

        {/* Net Payout */}
        <IndexTable.Cell>
          <Text tone={order.payout < 0 ? 'critical' : order.payout > 0 ? 'success' : 'base'} variant="bodyMd" fontWeight="medium">
            {apiClient.formatCurrency(order.payout || 0)}
          </Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          {/* wrap={false} matters: InlineStack wraps by default, and this column is
              narrow enough that the second icon dropped onto its own line — which is
              why the two icons were stacked instead of side by side. The wireframe has
              them inline with a 10px gap, vertically centred in the row.

              Only the eye icon opens the detail panel; the row itself is inert, as the
              wireframe has it. The stopPropagation calls below are kept deliberately —
              they cost nothing and mean restoring row-click cannot silently reintroduce
              the bug where "View in Shopify" also opened the panel. */}
          <InlineStack gap="250" wrap={false} blockAlign="center">
            <Tooltip content="View order details">
              <Button
                variant="tertiary"
                size="micro"
                icon={ViewIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOrderSelect(order.id);
                }}
              />
            </Tooltip>
            {/* Navigates out; stops the event for the reason given above. */}
            {order.brand?.shopDomain && order.shopifyOrderId && (
              <Tooltip content="View in Shopify">
                <Button
                  variant="tertiary"
                  size="micro"
                  icon={ExternalIcon}
                  url={`https://${order.brand.shopDomain}/admin/orders/${order.shopifyOrderId}`}
                  external
                  onClick={(e) => e.stopPropagation()}
                />
              </Tooltip>
            )}
          </InlineStack>
        </IndexTable.Cell>
      </IndexTable.Row>
    );

    // No client-side refund rows. The endpoint hardcodes `refunds: []` on every order and
    // returns refunds as their OWN rows via a SQL UNION, flagged with isRefundLineItem —
    // so this branch could never run. Roughly 90 lines of unreachable row-rendering,
    // including its own commission and return-fee arithmetic that would have silently
    // diverged from the backend's had it ever executed.

      return rows;
    });

    // Add summary row at the end if it exists
    if (summaryRow) {
      const summaryRowMarkup = (
        <IndexTable.Row
          id="SUMMARY_ROW"
          key="SUMMARY_ROW"
          selected={false}
          position={allRows.length}
          tone="subdued"
        >
          {/* Order - TOTAL label */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              TOTAL
            </Text>
          </IndexTable.Cell>

          {/* Date - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Brand - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Marketplace - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Source - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Status - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>

          {/* Weight */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {summaryRow.totalWeight ? apiClient.formatWeight(summaryRow.totalWeight) : '-'}
            </Text>
          </IndexTable.Cell>

          {/* Items */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {summaryRow.totalItems || 0}
            </Text>
          </IndexTable.Cell>

          {/* Sales (renamed from Amount) */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.grossAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Refund */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.refundAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Sales */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.netAmount || 0)}
            </Text>
          </IndexTable.Cell>

          {/* MKT Commission */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.appliedMarketplaceCommissionAmount ?? 0)}
            </Text>
          </IndexTable.Cell>

          {/* 2W Commission */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.applied2wCommissionAmount ?? 0)}
            </Text>
          </IndexTable.Cell>

          {/* Shipped by - blank. A totals row has no single answer, and leaving the cell
              out instead would shift every column after it by one. */}
          <IndexTable.Cell />

          {/* Shipping Fees */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.appliedShippingFee || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Return Fees */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold">
              {apiClient.formatCurrency(summaryRow.appliedReturnFee || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Net Payout */}
          <IndexTable.Cell>
            <Text variant="bodyMd" fontWeight="bold" tone={summaryRow.payout < 0 ? 'critical' : summaryRow.payout > 0 ? 'success' : 'base'}>
              {apiClient.formatCurrency(summaryRow.payout || 0)}
            </Text>
          </IndexTable.Cell>

          {/* Actions - empty */}
          <IndexTable.Cell>
            <Text variant="bodyMd">-</Text>
          </IndexTable.Cell>
        </IndexTable.Row>
      );

      return [...allRows, summaryRowMarkup];
    }

    return allRows;
  }, [orders, summaryRow, selectedResources, handleOrderSelect]);

  const emptyStateMarkup = (
    <EmptyState
      heading="No orders found"
      content={
        appliedFilters.length > 0
          ? "Try adjusting your search or filter criteria to find orders."
          : "Orders will appear here once they're synced from connected brands."
      }
      image="https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg"
      action={appliedFilters.length > 0 ? {
        content: 'Clear filters',
        onAction: handleFiltersClearAll,
      } : undefined}
    />
  );

  if (brandsLoading || (ordersLoading && currentPage === 1)) {
    return (
      <Page title="Order Management" fullWidth backAction={{ content: 'Dashboard', url: '/' }}>
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack align="center" gap="400">
                  <Spinner size="large" />
                  <BlockStack gap="200">
                    <Text variant="bodyLg" fontWeight="medium">
                      Loading orders...
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      Fetching order data across all brands
                    </Text>
                  </BlockStack>
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (ordersError) {
    return (
      <Page title="Order Management" fullWidth backAction={{ content: 'Dashboard', url: '/' }}>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <Text>Unable to load orders. {ordersError.message || 'Please try refreshing the page.'}</Text>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Order Management"
      fullWidth
      subtitle={totalItems > 0 ? `${totalItems.toLocaleString()} orders` : undefined}
      backAction={{ content: 'Dashboard', url: '/' }}
      secondaryActions={[
        {
          content: 'Refresh',
          icon: RefreshIcon,
          onAction: () => refetchOrders(),
          loading: ordersLoading,
          helpText: 'Refresh order data to get the latest information',
        },
      ]}
      compactTitle
    >
      <Layout>
        {/* Orders Table */}
        <Layout.Section>
          <Card>
            {/* Filters integrated into table card */}
            <Box padding="200">
              <Filters
                queryValue={searchQuery}
                queryPlaceholder="Search by order number, Mirakl ID or brand..."
                onQueryChange={setSearchQuery}
                onQueryClear={() => setSearchQuery('')}
                filters={filters}
                appliedFilters={appliedFilters}
                onClearAll={handleFiltersClearAll}
                disabled={ordersLoading}
              />
            </Box>
            <Divider />
            <IndexTable
              resourceName={resourceName}
              // The API's order total, not the length of this page's row array - which is 10
              // orders inflated by injected refund rows and the summary row. `totalItems` is
              // pagination.totalOrders and is already what the page subtitle uses.
              // 1 when there is nothing, so IndexTable still renders the header row: at
              // itemCount 0 it swaps the whole table for emptyState, headings included.
              // The empty state itself moved inside the table, below.
              itemCount={rowMarkup.length ? (totalItems || rowMarkup.length) : 1}
              // Headings, flags and labels all come off ORDER_COLUMNS, so they cannot
              // disagree about which column is at which index.
              headings={ORDER_HEADINGS}
              loading={ordersLoading}
              selectable={false}
              sortable={ORDER_SORTABLE}
              sortToggleLabels={SORT_TOGGLE_LABELS}
              sortDirection={sortDirection}
              sortColumnIndex={orderSortColumnIndex(sortBy)}
              onSort={handleSort}
              hasZebraStriping={true}
            >
              {rowMarkup.length === 0 ? (
                <IndexTable.Row id="empty-orders" position={0}>
                  <IndexTable.Cell colSpan={ORDER_HEADINGS.length}>{emptyStateMarkup}</IndexTable.Cell>
                </IndexTable.Row>
              ) : rowMarkup}
            </IndexTable>

            {/* Table Footer with Summary and Pagination */}
            {(!ordersLoading && orders.length > 0) && (
              <>
                <Divider />
                <Box padding="400">
                  <InlineStack gap="800" align="space-between" blockAlign="center">
                    {/* Rows per page, beside the pager. Kept even at 50 rows, where the pager
                        disappears: the control is how anyone gets back to a shorter window. */}
                    <PageSizeSelect value={itemsPerPage} onChange={handlePageSizeChange} />
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <Box style={{flexShrink: 0}}>
                        <InlineStack gap="200" blockAlign="center">
                          <Button
                            disabled={currentPage <= 1}
                            onClick={handlePreviousPage}
                            size="slim"
                          >
                            Previous
                          </Button>
                          <Text variant="bodySm" tone="subdued">
                            {currentPage} of {totalPages}
                          </Text>
                          <Button
                            disabled={currentPage >= totalPages}
                            onClick={handleNextPage}
                            size="slim"
                          >
                            Next
                          </Button>
                        </InlineStack>
                      </Box>
                    )}
                  </InlineStack>
                </Box>
              </>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      <OrderDetailPanel
        order={selectedOrder}
        open={orderDetailOpen}
        onClose={() => setOrderDetailOpen(false)}
      />
    </Page>
  );
}